import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  XCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

import { cn } from '@/utils'

const alertBannerVariants = cva(
  'relative flex w-full items-start gap-2 rounded-md border text-left',
  {
    variants: {
      variant: {
        info: 'border-semantic-info-border bg-semantic-info-subtle',
        success: 'border-semantic-success-border bg-semantic-success-subtle',
        warning: 'border-semantic-warning-border bg-semantic-warning-subtle',
        error: 'border-semantic-danger-border bg-semantic-danger-subtle',
      },
      size: {
        sm: 'px-3 py-2.5',
        md: 'px-4 py-3',
        lg: 'px-5 py-4',
      },
      hasDismiss: {
        true: 'pr-10',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'info',
      size: 'md',
      hasDismiss: false,
    },
  },
)

const iconWrapperVariants = cva(
  'mt-0.5 inline-flex shrink-0 items-center justify-center',
  {
    variants: {
      variant: {
        info: 'text-semantic-info-border',
        success: 'text-semantic-success-border',
        warning: 'text-semantic-warning-border',
        error: 'text-semantic-danger-border',
      },
      size: {
        sm: 'h-6 w-6',
        md: 'h-7 w-7',
        lg: 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'info',
      size: 'md',
    },
  },
)

const titleVariants = cva('leading-snug text-slate-900', {
  variants: {
    size: {
      sm: 'text-sm font-medium',
      md: 'text-base font-medium',
      lg: 'text-base font-semibold',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

const descriptionVariants = cva('mt-1 text-slate-700', {
  variants: {
    size: {
      sm: 'text-xs leading-relaxed',
      md: 'text-sm leading-relaxed',
      lg: 'text-sm leading-relaxed',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

export type AlertBannerProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertBannerVariants> & {
    title: React.ReactNode
    description?: React.ReactNode
    withIcon?: boolean
    dismiss?: () => void
  }

export const AlertBanner = React.forwardRef<HTMLDivElement, AlertBannerProps>(
  (
    {
      className,
      variant,
      size,
      title,
      description,
      withIcon = true,
      dismiss,
      ...props
    },
    ref,
  ) => {
    const hasDismiss = Boolean(dismiss)

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          alertBannerVariants({ variant, size, hasDismiss }),
          className,
        )}
        {...props}
      >
        {withIcon ? (
          <span
            aria-hidden="true"
            className={cn(iconWrapperVariants({ variant, size }))}
          >
            <AlertIcon variant={variant ?? 'info'} size={size ?? 'md'} />
          </span>
        ) : null}

        <div className="min-w-0 flex-1">
          <div className={cn(titleVariants({ size }))}>{title}</div>
          {description ? (
            <div className={cn(descriptionVariants({ size }))}>
              {description}
            </div>
          ) : null}
        </div>

        {dismiss ? (
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss alert"
            className={cn(
              'absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md',
              'text-slate-500 hover:bg-black/5 hover:text-slate-700',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/50',
            )}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    )
  },
)
AlertBanner.displayName = 'AlertBanner'

function AlertIcon({
  variant,
  size,
}: {
  variant: NonNullable<AlertBannerProps['variant']>
  size: NonNullable<AlertBannerProps['size']>
}) {
  const strokeWidth = size === 'sm' ? 2 : size === 'lg' ? 3 : 2.5
  switch (variant) {
    case 'success':
      return (
        <CheckCircleIcon
          className="h-full w-full"
          strokeWidth={strokeWidth}
          aria-hidden="true"
        />
      )
    case 'warning':
      return (
        <ExclamationCircleIcon
          className="h-full w-full"
          strokeWidth={strokeWidth}
          aria-hidden="true"
        />
      )
    case 'error':
      return (
        <XCircleIcon
          className="h-full w-full"
          strokeWidth={strokeWidth}
          aria-hidden="true"
        />
      )
    case 'info':
    default:
      return (
        <InformationCircleIcon
          className="h-full w-full"
          strokeWidth={strokeWidth}
          aria-hidden="true"
        />
      )
  }
}
